<template>
    <v-form class="d-flex align-center flex-wrap">
        <daterange
            :start-date-label="$t('date_from')"
            :end-date-label="$t('date_to')"
            :_start-date="dateFrom"
            :_end-date="dateTo"
            same-date
            :single-line="false"
            class="mx-1 flex-grow-0"
            content-class="date-field flex-grow-0"
            :hide-details="false"
            :end-date-days-offset="730"
            :min-start-date="minStartDate"
            @change-start-date="dateFrom = $event"
            @change-end-date="dateTo = $event"
        />
        <div class="d-flex flex-shrink-0">
            <v-checkbox
                v-for="(dayName, dayNumber) in weekdaysName"
                :key="dayNumber"
                v-model="dayOfWeek"
                :label="dayName"
                :value="dayNumber"
                class="mx-1"
            />
            <v-checkbox
                :model-value="dayOfWeek.length === 7"
                :indeterminate="dayOfWeek.length !== 7 && dayOfWeek.length !== 0"
                :label="`${$t('all')}/${$t('none')}`"
                class="text-capitalize mx-1"
                @update:model-value="toggleAllDays"
            />
        </div>
        <div class="d-flex flex-shrink-0 mx-2">
            <v-select
                v-if="false"
                v-model="subAllotmentId"
                :label="$t('sub_allotment')"
                :items="subAllotmentItems"
                class="mx-1"
                min-width="120"
            >
                <template v-slot:details>
                    <div class="d-flex full-width">
                        <v-spacer />
                        <v-btn size="x-small" color="primary" variant="text" @click="createSubAllotmentDialog = true">
                            {{ $t('create_suballotment') }}
                        </v-btn>
                    </div>
                </template>
            </v-select>
            <v-select
                v-if="mode !== 'set_release'"
                v-model="categoryId"
                :label="$t('category')"
                :items="categories"
                class="mx-1"
            />
            <v-select
                v-if="
                    mode === 'mass_edit' ||
                    mode === 'terminate_allotment' ||
                    mode === 'blockout' ||
                    mode === 'set_by_default'
                "
                v-model="serviceId"
                :label="$t('supplier.room_type')"
                :items="groupedRoomTypes"
                item-title="title"
                item-value="value"
                class="mx-1"
            >
                <template v-slot:item="{props, item}">
                    <v-list-item
                        :value="item.value"
                        :class="{'pl-8': item.raw.isIndented}"
                        :style="item.raw.isIndented ? 'border-left: 2px solid #e0e0e0; margin-left: 16px;' : ''"
                        @click="props.onClick"
                    >
                        <template v-if="item.raw.isIndented" v-slot:prepend>
                            <span class="text-grey-darken-1 mr-2">└─</span>
                        </template>
                        <v-list-item-title>{{ item.title }}</v-list-item-title>
                    </v-list-item>
                </template>
            </v-select>
        </div>
        <div v-if="mode === 'mass_edit'" class="d-flex flex-shrink-0 align-center">
            <v-radio-group v-model="updateMode" inline class="flex-shrink-0">
                <v-radio value="limited">
                    <template v-slot:label>
                        <div class="d-flex align-center">
                            <div>{{ $t('limited') }}</div>
                            <v-btn-toggle v-model="operationType" mandatory>
                                <v-btn icon="mdi-minus" value="DECREMENT" />
                                <v-btn icon="mdi-equal" value="NONE" />
                                <v-btn icon="mdi-plus" value="INCREMENT" />
                            </v-btn-toggle>
                            <v-number-input v-model="amount" density="compact" hide-details />
                        </div>
                    </template>
                </v-radio>
            </v-radio-group>
            <!-- <v-checkbox v-model="withOverbooking" :label="$t('allow_overbooking')" class="ms-1" /> -->
        </div>
        <v-number-input
            v-if="mode === 'set_release'"
            v-model="numberOfDays"
            :max-width="150"
            :min-width="150"
            class="mx-2"
            :label="capitalizeTFLOEW($t('day', 5))"
        />
        <v-spacer />
        <v-btn
            v-if="mode === 'mass_edit'"
            color="primary"
            class="mb-5 mx-2"
            :loading="loading"
            :disabled="loading"
            @click="update"
        >
            {{ $t('update') }}
        </v-btn>
        <template v-if="mode === 'terminate_allotment'">
            <v-btn
                color="primary"
                class="mb-5 mx-2"
                :loading="loading"
                :disabled="loading"
                @click="terminateAllotment()"
            >
                {{ $t('terminate_allotment') }}
            </v-btn>
            <v-btn
                class="mb-5 mx-2"
                :loading="loading"
                :disabled="loading"
                @click="terminateAllotment('CLEAR_TERMINATE_ALLOTMENT')"
            >
                {{ $t('reset_terminate_allotment') }}
            </v-btn>
        </template>
        <template v-if="mode === 'blockout'">
            <v-btn color="primary" class="mb-5 mx-2" :loading="loading" :disabled="loading" @click="setBlockout()">
                {{ $t('set_blockout') }}
            </v-btn>
            <v-btn class="mb-5 mx-2" :loading="loading" :disabled="loading" @click="setBlockout('CLEAR_BLOCK')">
                {{ $t('reset_blockout') }}
            </v-btn>
        </template>
        <v-btn
            v-if="mode === 'set_release'"
            color="primary"
            class="mb-5 mx-2"
            :loading="loading"
            :disabled="loading"
            @click="setRelease()"
        >
            {{ $t('update') }}
        </v-btn>
        <v-btn
            v-if="mode === 'set_by_default'"
            color="primary"
            class="mb-5 mx-2"
            :loading="loading"
            :disabled="loading"
            @click="setByDefault()"
        >
            {{ $t('update') }}
        </v-btn>
        <create-suballotment-dialog
            v-if="createSubAllotmentDialog"
            v-model="createSubAllotmentDialog"
            :loading="createSubAllotmentLoading"
            @submit="onCreateSuballotment"
        />
    </v-form>
</template>

<script setup lang="ts">
    import Daterange from '~/components/search/forms/Daterange.vue'
    import {capitalizeFirstLetter} from '~/utils/helpers'
    import CreateSuballotmentDialog from '~/components/products/CreateSuballotmentDialog.vue'
    import {capitalizeTheFirstLetterOfEachWord} from '@/utils/helpers'
    import {contractServiceType} from '~/utils/types/types'
    import {onMounted} from 'vue'
    import {supplierStore} from '~src/composables/store-accessor'

    const props = defineProps<{
        availability: any
        contract: any
        subAllotments: any
        mode: any
    }>()

    // Independent dates for Mass Edit form - always default to tomorrow and next month
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    tomorrow.setHours(0, 0, 0, 0)

    const nextMonth = new Date()
    nextMonth.setMonth(nextMonth.getMonth() + 1)
    nextMonth.setHours(0, 0, 0, 0)

    const dateFrom = ref(tomorrow)
    const dateTo = ref(nextMonth)

    const numberOfDays = ref(null)

    const createSubAllotmentDialog = ref(false)
    const createSubAllotmentLoading = ref(false)

    // Room type data for nested dropdown
    const roomTypes = ref([])
    const hotelServices = ref([])
    const roomTypesLoaded = ref(false)
    const hotelServicesLoaded = ref(false)

    // Remove the watcher that was syncing with parent component dates
    // Mass Edit form should have independent dates

    const {locale} = useI18n()
    const weekdaysName = ref([])
    const weekdaysArray = [...Array(7).keys()]
    const setupWeekdays = () => {
        const weekdays = new Intl.DateTimeFormat(locale.value, {weekday: 'short'})
        weekdaysName.value = [...weekdaysArray].map(day => weekdays.format(new Date(Date.UTC(2021, 5, day))))
    }
    watch(locale, setupWeekdays, {immediate: true})
    const dayOfWeek = ref([...weekdaysArray])
    function toggleAllDays() {
        if (dayOfWeek.value.length !== 7) {
            dayOfWeek.value = [...weekdaysArray]
        } else {
            dayOfWeek.value = []
        }
    }

    const subAllotmentId = ref('NETTO')
    const {t} = useI18n()

    const subAllotmentItems = computed(() => {
        return [
            {title: t('allotmentType.NETTO'), value: 'NETTO'},
            ...(props.subAllotments && props.subAllotments.map(el => ({title: el.name, value: el.id}))),
        ]
    })

    // Set minimum start date to tomorrow to prevent selection of today's date
    const minStartDate = computed(() => {
        const tomorrow = new Date()
        tomorrow.setDate(tomorrow.getDate() + 1)
        tomorrow.setHours(0, 0, 0, 0)
        return tomorrow
    })

    const onCreateSuballotment = async name => {
        try {
            createSubAllotmentLoading.value = true
            const rs = await app.$api.subAllotments.post({
                contractId: props.contract.id || app.$route.params?.contractId,
                name,
                active: true,
            })
            emit('create-suballotment', rs.id)
            subAllotmentId.value = rs.id
            createSubAllotmentDialog.value = false
        } catch (e) {
            app.$toast.error(t('error_message.save_error'))
        } finally {
            createSubAllotmentLoading.value = false
        }
    }

    const categories = computed(() => {
        const categories = props.availability.inventories.reduce((categories, {availabilityType}) => {
            availabilityType.forEach(({categoryId, categoryName}) => {
                if (!categories.find(({title}) => categoryName === title)) {
                    categories.push({value: categoryId, title: categoryName})
                }
            })
            return categories
        }, [])

        return categories
    })

    const categoryId = ref(categories.value[0].value)

    const isOwnHotel = computed(() => {
        return props.contract?.serviceType === 'ownHotel'
    })

    const services = computed(() => {
        const services = props.availability.inventories.reduce((services, {availabilityType}) => {
            availabilityType.forEach(({serviceId, serviceName, categoryId}) => {
                // For own hotels, don't filter by name to allow multiple services with same name but different sustainability levels
                // For other service types, keep the original behavior to avoid duplicates
                if (isOwnHotel.value) {
                    // For own hotels, check by serviceId instead of name to avoid filtering out sustainability variants
                    if (!services.find(({value}) => serviceId === value)) {
                        services.push({value: serviceId, title: serviceName, categoryId})
                    }
                } else {
                    // Original behavior for non-hotel services
                    if (!services.find(({title}) => serviceName === title)) {
                        services.push({value: serviceId, title: serviceName, categoryId})
                    }
                }
            })
            return services
        }, [])
        // QA follow-up: with a category selected, the Room Type list must only offer rooms of that
        // category. Rooms whose category has no availability data (and is therefore not listed in
        // the Category dropdown) stay visible so they can still be managed (ZSH-873 spirit).
        const listedCategoryIds = new Set(categories.value.map(category => category.value))
        return services.filter(
            service =>
                !service.categoryId ||
                !listedCategoryIds.has(service.categoryId) ||
                !categoryId.value ||
                service.categoryId === categoryId.value
        )
    })

    // Set default to first BASIC room type, or first available if no BASIC found
    const getDefaultServiceId = () => {
        const filteredServices = services.value.filter(service => {
            if (service.categoryId && categoryId.value && service.categoryId !== categoryId.value) return false
            // ZSH-873: never default to a disabled room type
            if (getRoomTypeByServiceId(service.value)?.active === false) return false
            return true
        })

        if (filteredServices.length === 0) return null

        // For own hotels, try to find a BASIC room type
        if (isOwnHotel.value && roomTypesLoaded.value && hotelServicesLoaded.value) {
            const basicService = filteredServices.find(service => {
                const roomType = getRoomTypeByServiceId(service.value)
                return roomType?.sustainabilityLevel === 'BASIC'
            })
            if (basicService) return basicService.value
        }

        // Fallback to first available service
        return filteredServices[0]?.value || null
    }

    const serviceId = ref(getDefaultServiceId())
    // Keep the Category dropdown in sync with the selected room: the room list is not filtered
    // by category (ZSH-873), so without the sync the dropdown can lag behind the selection and
    // mislead. The request itself already carries the room's real category.
    let syncingCategoryToRoom = false
    watch(categoryId, () => {
        if (syncingCategoryToRoom) return
        serviceId.value = getDefaultServiceId()
    })
    watch(serviceId, newId => {
        if (!isOwnHotel.value || !newId) return
        const roomCategory = getSelectedServiceCategoryId()
        if (roomCategory && roomCategory !== categoryId.value) {
            syncingCategoryToRoom = true
            categoryId.value = roomCategory
            nextTick(() => {
                syncingCategoryToRoom = false
            })
        }
    })

    // Watch for data loading completion to update default selection
    watch([roomTypesLoaded, hotelServicesLoaded], () => {
        if (roomTypesLoaded.value && hotelServicesLoaded.value) {
            serviceId.value = getDefaultServiceId()
        }
    })

    // Load room type and hotel service data on mount
    onMounted(async () => {
        if (isOwnHotel.value) {
            await loadDataFromSupplierStore()
        }
    })

    async function loadDataFromSupplierStore() {
        // ZSH-873: always load the full room-type list (incl. inactive) from the API.
        // The supplier-store list is active-only, so it lacks the `active` flag needed
        // to filter disabled rooms out of the dropdown.
        await loadRoomTypesFromAPI()

        // Load hotel services from API (they're not stored in supplier store)
        await loadHotelServices()
    }

    async function loadRoomTypesFromAPI() {
        try {
            // Get hotel ID from supplier store (this should be the correct one)
            const hotelId = supplierStore.hotelInfo?.hotelId

            if (!hotelId) {
                roomTypesLoaded.value = true
                return
            }

            const response = await app.$api.hotelDescriptionRoomType.get(hotelId, {locale: 'all'})
            roomTypes.value = response.hotelRoomTypes || []
            roomTypesLoaded.value = true
        } catch (error) {
            console.error('Error loading room types:', error)
            roomTypes.value = []
            roomTypesLoaded.value = true
        }
    }

    async function loadHotelServices() {
        if (hotelServicesLoaded.value || !isOwnHotel.value) return

        try {
            const response = await app.$api.ownHotelServices.get({contractId: props.contract?.id})
            hotelServices.value = response.services || []
            hotelServicesLoaded.value = true
        } catch (error) {
            console.error('Error loading hotel services:', error)
            hotelServices.value = []
            hotelServicesLoaded.value = true
        }
    }

    function getRoomTypeByServiceId(serviceId) {
        // First try to find the service in hotel services to get the room type ID
        const hotelService = hotelServices.value.find(service => service.id === serviceId)

        if (hotelService && hotelService.hotelRoomTypeId) {
            // Use the hotelRoomTypeId from hotel service to find the room type
            const roomType = roomTypes.value.find(rt => rt.id === hotelService.hotelRoomTypeId)
            if (roomType) {
                return roomType
            }
        }

        // Fallback: try direct serviceId match with room type ID
        const directMatch = roomTypes.value.find(rt => rt.id === serviceId)
        if (directMatch) {
            return directMatch
        }

        return null
    }

    const groupedRoomTypes = computed(() => {
        // If not a hotel or data not loaded yet, return services with sustainability levels
        if (!isOwnHotel.value || !roomTypesLoaded.value || !hotelServicesLoaded.value) {
            if (!services.value || services.value.length === 0) {
                return []
            }
            // For non-hotels or when data not loaded, still show sustainability levels if available
            const servicesWithLevels = services.value
                .filter(service => service.value !== 0) // Remove "All" option
                .map(service => {
                    const roomType = getRoomTypeByServiceId(service.value)
                    const sustainabilityLevel = roomType?.sustainabilityLevel || 'BASIC'
                    const baseName = roomType?.name?.en || service.title

                    return {
                        ...service,
                        title: `${baseName} (${sustainabilityLevel}) [ID: ${service.value}]`,
                        sustainabilityLevel,
                        isIndented: false,
                    }
                })
            return servicesWithLevels
        }

        // ZSH-873: group by basicRoomId so SUSTAINABLE/ZERO rooms always nest under their real
        // BASIC parent (BASIC, then SUSTAINABLE, then ZERO per group). The previous flat name-sort
        // placed a special under an unrelated basic room whenever the BASIC room name differed
        // from its specials (e.g. "Waterfall view SMTH BASIC" sorted after "Waterfall view SMTH").
        // Mirrors groupedServicesData / getBasicRoomGroups.
        const groups = new Map() // key -> {name, basic, sustainable[], zero[]}
        const addedServiceIds = new Set()
        // Categories present in the Category dropdown (only those with availability data).
        // A room whose own category has no availability must still be selectable, so we only
        // exclude rooms in a DIFFERENT listed category — not unlisted ones (ZSH-873).
        const listedCategoryIds = new Set(categories.value.map(c => c.value))

        const ensureGroup = (key, name) => {
            if (!groups.has(key)) groups.set(key, {name: name || '', basic: null, sustainable: [], zero: []})
            const g = groups.get(key)
            if (name && !g.name) g.name = name
            return g
        }

        const pushItem = (value, name, roomType) => {
            if (roomType?.active === false) return // ZSH-873: hide confirmed-disabled room types
            const sustainabilityLevel = roomType?.sustainabilityLevel || 'BASIC'
            const baseName = roomType?.name?.en || name
            const item = {
                value,
                title: `${baseName} (${sustainabilityLevel}) [ID: ${value}]`,
                originalTitle: name,
                sustainabilityLevel,
                isIndented: sustainabilityLevel !== 'BASIC',
            }

            // Room type can't be resolved (service missing from hotelServices or no hotelRoomTypeId).
            // Keep the room visible as a standalone entry instead of dropping it — otherwise a BASIC
            // room with no SUSTAINABLE/ZERO siblings vanishes from the dropdown entirely.
            if (!roomType) {
                const group = ensureGroup(`standalone_${value}`, baseName)
                group.basic = item
                group.name = baseName
                return
            }

            if (sustainabilityLevel === 'BASIC') {
                const group = ensureGroup(roomType.id, baseName)
                group.basic = item
                group.name = baseName
            } else {
                const parentId = roomType.basicRoomId
                if (parentId) {
                    const group = ensureGroup(parentId, baseName)
                    if (sustainabilityLevel === 'SUSTAINABLE') group.sustainable.push(item)
                    else group.zero.push(item)
                } else {
                    // No parent reference: keep it top-level so it never nests under the wrong basic
                    const group = ensureGroup(`standalone_${value}`, baseName)
                    ;(sustainabilityLevel === 'SUSTAINABLE' ? group.sustainable : group.zero).push({
                        ...item,
                        isIndented: false,
                    })
                }
            }
        }

        // Services that already have availability data
        services.value.forEach(service => {
            if (service.value === 0 || addedServiceIds.has(service.value)) return
            addedServiceIds.add(service.value)
            pushItem(service.value, service.title, getRoomTypeByServiceId(service.value))
        })

        // Hotel services without availability yet (newly created rooms), respecting category filter
        hotelServices.value.forEach(hotelService => {
            if (addedServiceIds.has(hotelService.id)) return
            if (
                categoryId.value &&
                hotelService.categoryId &&
                listedCategoryIds.has(hotelService.categoryId) &&
                hotelService.categoryId !== categoryId.value
            )
                return
            addedServiceIds.add(hotelService.id)
            pushItem(
                hotelService.id,
                hotelService.name,
                roomTypes.value.find(rt => rt.id === hotelService.hotelRoomTypeId)
            )
        })

        // Flatten: groups sorted by BASIC room name; BASIC, then SUSTAINABLE, then ZERO per group.
        // Specials in a parentless group render top-level (non-indented) to avoid wrong nesting.
        const roomTypeServices = []
        Array.from(groups.values())
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
            .forEach(group => {
                const hasBasic = Boolean(group.basic)
                if (hasBasic) roomTypeServices.push(group.basic)
                const children = [...group.sustainable, ...group.zero]
                children.forEach(item => {
                    roomTypeServices.push(hasBasic ? item : {...item, isIndented: false})
                })
            })

        return roomTypeServices
    })

    const updateMode = ref('limited')
    const amount = ref(1)
    const operationType = ref('NONE')
    const withOverbooking = ref(false)

    const app = useNuxtApp()
    const loading = ref(false)
    const emit = defineEmits<{
        (e: 'update'): void
        (e: 'create-suballotment', value): void
    }>()
    // Mass edit always targets a single room type, so the request must carry THAT room's category —
    // not the value of the Category dropdown. The dropdown defaults to the first category and,
    // since ZSH-873, no longer filters the room list, so it can diverge from the selected room.
    // The backend resolves the basic room for the special-room validation (special <= basic)
    // through a map scoped by the sent category: a mismatched category left the room unresolved
    // and the validation silently passed, storing special > basic values with a 200 OK.
    function getSelectedServiceCategoryId() {
        if (!serviceId.value) return categoryId.value || null
        const fromAvailability = services.value.find(service => service.value === serviceId.value)?.categoryId
        if (fromAvailability) return fromAvailability
        return hotelServices.value.find(hotelService => hotelService.id === serviceId.value)?.categoryId ?? null
    }

    function getBasicSiblingServiceId(roomType) {
        if (!roomType?.basicRoomId) return null
        const viaHotelService = hotelServices.value.find(
            hotelService => hotelService.hotelRoomTypeId === roomType.basicRoomId
        )
        if (viaHotelService) return viaHotelService.id
        const viaAvailability = services.value.find(
            service => getRoomTypeByServiceId(service.value)?.id === roomType.basicRoomId
        )
        return viaAvailability?.value ?? null
    }

    function getNettoAvailabilityForDay(lookupServiceId, day) {
        const allotment = props.availability?.inventories?.find(inventory => inventory.name === 'Allotment')
        if (!allotment) return null
        return (
            allotment.availabilityType.find(
                availability =>
                    availability.serviceId === lookupServiceId &&
                    availability.allotmentType === 'NETTO' &&
                    new Date(`${availability.dateFrom}T00:00:00`) <= day &&
                    day <= new Date(`${availability.dateTo}T23:59:59`)
            ) ?? null
        )
    }

    // Client-side mirror of the backend rule "special room availability <= basic room availability".
    // Blocks the request before it reaches the server (values are never applied) for days covered by
    // the loaded availability data. Days without loaded data are left to the server-side validation.
    // Mirrors the backend semantics: UNLIMITED basic always passes, INCREMENT validates the resulting
    // value (current + amount), DECREMENT and closing the room (SET 0) can never raise the special room.
    function validateSpecialRoomAgainstBasic() {
        if (!isOwnHotel.value || !serviceId.value) return null
        const amountValue = Number(amount.value)
        if (!Number.isFinite(amountValue)) return null

        const roomType = getRoomTypeByServiceId(serviceId.value)
        if (!roomType || roomType.sustainabilityLevel === 'BASIC') return null
        if (operationType.value === 'DECREMENT' || amountValue === 0) return null

        const basicServiceId = getBasicSiblingServiceId(roomType)
        if (!basicServiceId) return null

        const start = new Date(dateFrom.value)
        start.setHours(0, 0, 0, 0)
        const end = new Date(dateTo.value)
        end.setHours(0, 0, 0, 0)

        const cursor = new Date(start)
        while (cursor <= end) {
            const day = new Date(cursor)
            // weekdaysArray[i] is the weekday of Date.UTC(2021, 5, i) = May 31 - Jun 6, 2021 = Mon..Sun
            const weekdayIndex = (day.getDay() + 6) % 7
            if (dayOfWeek.value.includes(weekdayIndex)) {
                const basic = getNettoAvailabilityForDay(basicServiceId, day)
                if (basic) {
                    let effectiveAmount = amountValue
                    if (operationType.value === 'INCREMENT') {
                        const special = getNettoAvailabilityForDay(serviceId.value, day)
                        if (!special) {
                            cursor.setDate(cursor.getDate() + 1)
                            continue
                        }
                        effectiveAmount = (special.availableQuantity || 0) + amountValue
                    }
                    const basicIsUnlimited = basic.status === 'UNLIMITED'
                    const basicIsEnough =
                        ['AVAILABLE', 'OVERBOOKING'].includes(basic.status) &&
                        (basic.availableQuantity || 0) >= effectiveAmount
                    if (!basicIsUnlimited && !basicIsEnough) {
                        const dayLabel = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`
                        return (
                            `Special room availability should be <= basic room availability, date: ${dayLabel}, ` +
                            `basic allotment type: NETTO, basic available quantity: ${basic.availableQuantity ?? 0}`
                        )
                    }
                }
            }
            cursor.setDate(cursor.getDate() + 1)
        }
        return null
    }

    async function update() {
        const validationError = validateSpecialRoomAgainstBasic()
        if (validationError) {
            app.$toast.error(validationError)
            return
        }
        loading.value = true
        const requestCategoryId = getSelectedServiceCategoryId()
        const rq = [
            {
                dayOfWeek: weekdaysArray.map(index => dayOfWeek.value.includes(index)),
                dateFrom: dateFrom.value,
                dateTo: dateTo.value,
                ...(serviceId.value && {serviceId: serviceId.value}),
                ...(requestCategoryId && {categoryId: requestCategoryId}),
                amount: amount.value,
                operation: withOverbooking.value ? 'SET_WITH_OVERBOOKING' : 'SET',
                ...(operationType.value !== 'NONE' && {operationType: operationType.value}),
                ...(subAllotmentId.value !== 'NETTO' && {subAllotmentId: subAllotmentId.value}),
            },
        ]
        try {
            await app.$api.availability.put(props.contract.id, rq)
            emit('update')
        } catch (e) {
            if (e?.errors) {
                e.errors.forEach(error => {
                    if (error.errorType === 'validation' && error.message) {
                        app.$toast.error(error.message)
                    } else {
                        app.$toast.error(t('error_message.save_error'))
                    }
                })
            } else {
                app.$toast.error(t('error_message.save_error'))
            }
        } finally {
            loading.value = false
        }
    }

    async function terminateAllotment(operation = 'STOP') {
        loading.value = true
        const rq = [
            {
                dayOfWeek: weekdaysArray.map(index => dayOfWeek.value.includes(index)),
                dateFrom: dateFrom.value,
                dateTo: dateTo.value,
                ...(categoryId.value && {categoryId: categoryId.value}),
                operation,
            },
        ]
        try {
            await app.$api.availability.put(props.contract.id, rq)
            emit('update')
        } catch (e) {
            if (e?.errors) {
                e.errors.forEach(error => {
                    if (error.errorType === 'validation' && error.message) {
                        app.$toast.error(error.message)
                    } else {
                        app.$toast.error(t('error_message.save_error'))
                    }
                })
            } else {
                app.$toast.error(t('error_message.save_error'))
            }
        } finally {
            loading.value = false
        }
    }

    async function setBlockout(operation = 'BLOCK') {
        loading.value = true
        const rq = [
            {
                dayOfWeek: weekdaysArray.map(index => dayOfWeek.value.includes(index)),
                dateFrom: dateFrom.value,
                dateTo: dateTo.value,
                ...(categoryId.value && {categoryId: categoryId.value}),
                operation,
            },
        ]
        try {
            await app.$api.availability.put(props.contract.id, rq)
            emit('update')
        } catch (e) {
            if (e?.errors) {
                e.errors.forEach(error => {
                    if (error.errorType === 'validation' && error.message) {
                        app.$toast.error(error.message)
                    } else {
                        app.$toast.error(t('error_message.save_error'))
                    }
                })
            } else {
                app.$toast.error(t('error_message.save_error'))
            }
        } finally {
            loading.value = false
        }
    }

    async function setRelease(operation = 'RELEASE') {
        loading.value = true
        const rq = [
            {
                dayOfWeek: weekdaysArray.map(index => dayOfWeek.value.includes(index)),
                dateFrom: dateFrom.value,
                dateTo: dateTo.value,
                amount: numberOfDays.value,
                operation,
            },
        ]
        try {
            await app.$api.availability.put(props.contract.id, rq)
            emit('update')
        } catch (e) {
            if (e?.errors) {
                e.errors.forEach(error => {
                    if (error.errorType === 'validation' && error.message) {
                        app.$toast.error(error.message)
                    } else {
                        app.$toast.error(t('error_message.save_error'))
                    }
                })
            } else {
                app.$toast.error(t('error_message.save_error'))
            }
        } finally {
            loading.value = false
        }
    }

    async function setByDefault(operation = 'RESET') {
        loading.value = true
        const rq = [
            {
                dayOfWeek: weekdaysArray.map(index => dayOfWeek.value.includes(index)),
                dateFrom: dateFrom.value,
                dateTo: dateTo.value,
                ...(categoryId.value && {categoryId: categoryId.value}),
                operation,
            },
        ]
        try {
            await app.$api.availability.put(props.contract.id, rq)
            emit('update')
        } catch (e) {
            if (e?.errors) {
                e.errors.forEach(error => {
                    if (error.errorType === 'validation' && error.message) {
                        app.$toast.error(error.message)
                    } else {
                        app.$toast.error(t('error_message.save_error'))
                    }
                })
            } else {
                app.$toast.error(t('error_message.save_error'))
            }
        } finally {
            loading.value = false
        }
    }

    function capitalizeTFLOEW(text) {
        return capitalizeTheFirstLetterOfEachWord(text)
    }

    const isShowSubAllotment = computed(() => {
        if (props.contract.serviceType === contractServiceType.ownVisa) {
            return false
        }

        return (
            props.mode === 'mass_edit' ||
            props.mode === 'terminate_allotment' ||
            props.mode === 'blockout' ||
            props.mode === 'set_by_default'
        )
    })
</script>
